/**
 * Registers the Tenger brand faces so the widget's own styles can reference
 * them by name — as `FT Poppins` / `FT Montserrat`, NOT as `Poppins` /
 * `Montserrat`.
 *
 * Registering on the host document is the ONE thing the widget deliberately
 * adds to the customer's page, and it has to be: an `@font-face` rule declared
 * inside a shadow root is not reliably honoured in Chromium, because font faces
 * resolve against the document rather than the shadow tree. `document.fonts`
 * is the only reliable place to put them.
 *
 * That makes the family names the whole safety story, which is why they are
 * namespaced `FT `. Adding a face DOES change how the host page paints —
 * making a family name resolvable is precisely how a font takes effect — and
 * registering the public names would have caused two real regressions on
 * customer sites:
 *
 *  1. A page whose CSS already says `font-family: Poppins, Arial` but that
 *     ships no Poppins renders Arial today. Register `Poppins` and the name
 *     starts resolving document-wide, repainting THEIR ENTIRE PAGE in our
 *     face the moment the widget loads.
 *  2. Our files are the *latin* subsets and carry no `unicodeRange`, so each
 *     face claims `U+0-10FFFF`. A page already using Poppins latin-ext
 *     (Polish, Czech, Turkish) would gain a same-family/same-weight face
 *     missing exactly those glyphs, and its text would fall through to the
 *     next family for those characters alone.
 *
 * `FT Poppins` collides with nothing, so neither can happen. `brand.ts` still
 * lists the public `Poppins`/`Montserrat` immediately after the private names
 * in its stacks, so a host page that genuinely ships the real face can serve
 * the glyphs our subsets lack.
 *
 * Note `destroy()` (see `widget-controller.ts`) does NOT unregister these
 * faces — the Font Loading API entries outlive the widget's shadow root for
 * the life of the document. With private family names that residue is inert:
 * nothing on the host page references `FT Poppins`, so nothing repaints.
 * Removal is deliberately not implemented rather than being an oversight.
 *
 * Fail-silent throughout, matching the widget's policy toward the host app: a
 * missing Font Loading API, a blocked `font-src`, or a rejected fetch all leave
 * the stack falling through to Helvetica Neue / Arial rather than surfacing an
 * error into the customer's page.
 */

interface BrandFace {
  family: string;
  file: string;
  weight: string;
}

/**
 * The `FT ` prefix is load-bearing, not decoration — see the module comment.
 * These strings must stay in lockstep with `BRAND_FONT_STACK` /
 * `BRAND_DISPLAY_STACK` in `brand.ts`; a face nothing references is a fetch
 * for nothing, and a stack naming a face nothing registers silently falls
 * through to Arial.
 */
const BRAND_FACES: BrandFace[] = [
  { family: 'FT Poppins', file: 'poppins-latin-400-normal.woff2', weight: '400' },
  { family: 'FT Poppins', file: 'poppins-latin-600-normal.woff2', weight: '600' },
  { family: 'FT Montserrat', file: 'montserrat-latin-700-normal.woff2', weight: '700' },
];

let registered = false;

/** Resets the once-only guard. Test-only. */
export function resetBrandFontsForTest(): void {
  registered = false;
}

export function loadBrandFonts(apiBase: string): void {
  if (registered) return;
  registered = true;

  try {
    if (typeof FontFace === 'undefined' || !document.fonts) return;

    const base = `${apiBase.replace(/\/+$/, '')}/api/v1/widget/fonts`;

    for (const face of BRAND_FACES) {
      const fontFace = new FontFace(face.family, `url(${base}/${face.file}) format('woff2')`, {
        weight: face.weight,
        style: 'normal',
        display: 'swap',
      });
      document.fonts.add(fontFace);
      // Kick off the fetch; a rejection is expected and harmless when the host
      // page's CSP blocks our origin as a font source.
      void fontFace.load().catch(() => undefined);
    }
  } catch {
    // Locked-down host, missing document, or a hostile `document.fonts` shim.
  }
}
